import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIError, APIResponse } from "@/types/custom-http";
import { systemSettingsApi } from "./systems-settingsApi";
import {
  SystemSetting,
  CreateSystemSetting,
  UpdateSystemSetting,
} from "./systems-settings.interface";

const buildReject = (error: unknown, fallbackMessage: string) => {
  const apiError = error as APIError;
  const { status, data } = apiError;
  return {
    status,
    message: data?.message || fallbackMessage,
    details: data?.details,
  };
};

export const fetchSystemSettings = createAsyncThunk<
  APIResponse<SystemSetting[]>,
  void,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/fetchSystemSettings", async (_, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(systemSettingsApi.endpoints.getSystemSettings.initiate()).unwrap();
    return result;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al obtener configuraciones"));
  }
});

export const fetchSystemSettingById = createAsyncThunk<
  APIResponse<SystemSetting>,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/fetchSystemSettingById", async (id, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(systemSettingsApi.endpoints.getSystemSettingById.initiate(id)).unwrap();
    return result;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al obtener configuración"));
  }
});

export const fetchSystemSettingsBySection = createAsyncThunk<
  APIResponse<SystemSetting[]>,
  string,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/fetchSystemSettingsBySection", async (section, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(systemSettingsApi.endpoints.getSystemSettingsBySection.initiate(section)).unwrap();
    return result;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al obtener configuraciones por sección"));
  }
});

export const createSystemSetting = createAsyncThunk<
  APIResponse<SystemSetting>,
  CreateSystemSetting,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/createSystemSetting", async (data, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(systemSettingsApi.endpoints.createSystemSetting.initiate(data)).unwrap();
    return result;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al crear configuración"));
  }
});

export const updateSystemSetting = createAsyncThunk<
  APIResponse<SystemSetting>,
  { id: number; changes: UpdateSystemSetting },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/updateSystemSetting", async ({ id, changes }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(systemSettingsApi.endpoints.updateSystemSetting.initiate({ id, changes })).unwrap();
    return result;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al actualizar configuración"));
  }
});

export const deleteSystemSetting = createAsyncThunk<
  number,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("systemSettings/deleteSystemSetting", async (id, { dispatch, rejectWithValue }) => {
  try {
    await dispatch(systemSettingsApi.endpoints.deleteSystemSetting.initiate(id)
      ).unwrap();
    return id;
  } catch (error) {
    return rejectWithValue(buildReject(error, "Error al eliminar configuración"));
  }
});
